import { api } from "./api";

/**
 * Two server-side spellings that the frontend has to match exactly, declared
 * here so each is stated once instead of being repeated at the call sites.
 *
 * Verified against the live API on 2026-08-13: the correctly-spelled
 * `/admin/getDeliveryMeals` returns "Page not found", so the misspelling is not
 * yet aliased. Because this backend answers 404s with HTTP 200, switching to the
 * correct spelling ahead of the backend would not throw — the delivery list
 * would just come back empty.
 *
 * Both renames are backend-led: add the correctly-spelled route as an alias,
 * flip the constant here, then retire the old path.
 */
const DELIVERY_MEALS = "/admin/getDeilveryMeals"; // server spells it "Deilvery"
const CAROUSEL = "coursel"; // server spells it "coursel", not "carousel"

export const GetCustFullProfileApi = (params: any) => {
  return api.post(`/cust/get/full-profile`,params);
};

export const UpdateCustFullProfileApi = (params: any) => {
  return api.post(`/cust/update/full-profile`,params);
};
export const GetMedicalHistoryApi = (params: any) => {
  return api.get(`/cust/get/medical-history?cmr_id=${params.cmr_id}`);
};
export const UpdateMedicalHistoryApi = (params: any) => {
  return api.post(`/cust/admin/update/medical-history`,params);
};

export const GetReportsApi = (params: any) => {
  return api.post(`/cust/get/reports`,params);
};

export const DeleteReportApi = (params: any) => {
  return api.post(`/cust/delete/${params.report_id}/reports`);
};

export const GetFoodLifestyleApi = (params: any) => {
  return api.post(`/cust/get/food-lifestyle`, { cmr_id: params.cmr_id.id });
};

export const UpdateFoodLifestyleApi = (params: any) => {
  return api.post(`/cust/update/food-lifestyle`, params);
};

export const GetRecallApi = (params: any) => {
  return api.post(`/cust/get/recall`, params);
};

export const UpdateRecallApi = (params: any) => {
  return api.post(`/cust/update/recall`, params);
};

export const GetActivityByDateApi = (params: any) => {
  return api.post(`/end/cust/getAcitivtyByDate`, params);
};

export const SetActivityGoalApi = (params: any) => {
  return api.post(`/end/cust/SetActivityGoal`, params);
};

export const GetFirstLastMeasureApi = (params: any) => {
  return api.post(`/cust/measure/FirstLast`,  params );
};

/**
 * The Progress tab's measurement history.
 *
 * POST with cmr_id in the body, matching GetAllBodyMeasuresApi below — the
 * Measurements tab calls its own getAllOfCustomer that way and it returns data.
 * As a GET this path answers the API's catch-all, `{"status":false,"message":
 * "Page not found"}`, and because that arrives as HTTP 200 with an ETag the saga
 * gate reads `status: false` and reports a failure while the network panel shows
 * a healthy 200/304. Verified 26 Aug 2026: of GET/POST/PUT/PATCH/DELETE on this
 * path, only POST reaches the auth middleware.
 */
export const GetAllMeasuresApi = (params: any) => {
  return api.post(`/cust/measure/getAllOfCustomer`, params);
};

export const DeleteMeasureByIdApi = (params: any) => {
  return api.post(`/cust/measure/deleteById`, params);
};

export const GetBodyStartedCurrentApi = (params: any) => {
  return api.post(`/body/started-current`, params);
};

export const GetAllBodyMeasuresApi = (params: any) => {
  return api.post(`/cust/body-measurement/getAllOfCustomer`, params);
};

export const DeleteBodyMeasureByIdApi = (params: any) => {
  return api.post(`/cust/body-measurement/deleteById`, params);
};

export const GetAllLeadsApi = (params?: { rows?: number; page?: number }) => {
  const queryParams = new URLSearchParams();
  if (params?.rows) queryParams.append('rows', params.rows.toString());
  if (params?.page) queryParams.append('page', params.page.toString());
  const queryString = queryParams.toString();
  return api.get(`/general/getAllLeads${queryString ? `?${queryString}` : ''}`);
};

export const GetAllCustomersApi = (params?: { rows?: number; page?: number }) => {
  const queryParams = new URLSearchParams();
  if (params?.rows) queryParams.append('rows', params.rows.toString());
  if (params?.page) queryParams.append('page', params.page.toString());
  const queryString = queryParams.toString();
  return api.get(`/general/getAllCustomers${queryString ? `?${queryString}` : ''}`);
};

/**
 * Who has registered for a program, or booked an appointment.
 *
 *   POST /programs/enrolledUsers   { prog_id,    date?, page, limit }
 *   POST /session/bookedUsers      { session_id, date?, page, limit }
 *
 * Both probed live 11 Sep 2026. Admin token required — anonymous gets
 * "Token is required". Both answer with the same envelope:
 *
 *   data.users        the people
 *   data.pagination   { total, limit, offset, current_page, total_pages, has_more }
 *
 * (snake_case `total_pages`, matching the food-log routes rather than the
 * camelCase `totalPages` the customer list uses.) No cap on `limit`: 500 is
 * honoured, unlike /auth/customer/list which silently clamps to 100.
 *
 * `date` IS optional on both — omit it, or send "" or null, and the whole
 * registered list comes back. Verified all three ways.
 *
 * What `date` actually means differs from what the name suggests, and it
 * matters for the screen's wording. For a program it filters on the
 * ENROLLMENT'S VALIDITY WINDOW, not on the program's session weekdays: the live
 * enrollment (start_date 30 Sep, valid_till 28 Aug 2028) is returned for
 * 2026-10-13, a Tuesday the Monday-only program does not run on, and excluded
 * for 2020-01-01 and 2030-01-01. So it answers "who was enrolled on that day",
 * not "who attends that day". For an appointment `date` is the booked date
 * itself, so it does mean what it looks like (2 bookings in total, 1 on
 * 2026-09-08).
 *
 * A malformed date is NOT rejected cleanly — it leaks the driver error
 * `invalid input syntax for type date: "not-a-date"` with status false. Only
 * ever send YYYY-MM-DD, which is what <input type="date"> produces.
 *
 * The id is required and reported plainly ("prog_id is required" /
 * "session_id is required"); an id that matches nothing returns an empty list
 * with status true, so "no registrations" and "bad id" look the same.
 */
export const GetProgramEnrolledUsersApi = (params: {
  prog_id: string;
  date?: string;
  page?: number;
  limit?: number;
}) => {
  return api.post(`/programs/enrolledUsers`, {
    prog_id: params.prog_id,
    // Omitted entirely when blank rather than sent empty — the endpoint happens
    // to accept "" as "no filter", but there is no reason to depend on that.
    ...(params.date?.trim() ? { date: params.date.trim() } : {}),
    page: params.page ?? 1,
    limit: params.limit ?? 20,
  });
};

export const GetSessionBookedUsersApi = (params: {
  session_id: string;
  date?: string;
  page?: number;
  limit?: number;
}) => {
  return api.post(`/session/bookedUsers`, {
    session_id: params.session_id,
    ...(params.date?.trim() ? { date: params.date.trim() } : {}),
    page: params.page ?? 1,
    limit: params.limit ?? 20,
  });
};

/**
 * Every customer account, for the User History screen.
 *
 *   GET /auth/customer/list?page=1&limit=100
 *
 * These are APP users, keyed by `cmr_id` — everyone who has signed up on the
 * phone, whether or not they ever bought a program or an appointment. 113
 * records when probed live on 10 Sep 2026.
 *
 * NOT to be confused with GET /auth/user/list, which looks similar but returns
 * the 11 CONSOLE accounts (superadmin, practitioner, delivery) that can sign in
 * to this admin panel. Different table, different key, different people.
 *
 * The useful history fields on a record:
 *
 *   ismobilelogin   has signed in on the mobile app
 *   islead          still a lead, as opposed to a converted customer
 *   onboarded       finished onboarding
 *   created_at      when they signed up
 *   updated_at      when the record last changed
 *
 * PARAMETERS — and this is the important part, because almost nothing is
 * supported. Only three do anything:
 *
 *   page               1-based
 *   limit              hard-capped at 100; asking for 150 or 500 returns 100
 *   cmr_phone_number   partial substring match ("3779" matches mid-number)
 *
 * There is NO name or email filter. Thirty-odd candidate names were tried —
 * customer_name, cmr_first_name, name, user_name, cmr_email, search, q, term,
 * keyword and the rest — and every one returns the full unfiltered 113, exactly
 * as an invented parameter name does. An ignored parameter is indistinguishable
 * from a working one unless you check the count, which is why the screen does
 * its name and email searching client-side instead of trusting a filter that
 * looks plausible.
 *
 * A page past the end returns an empty list rather than an error. POST is not
 * routed at all ("Page not found"), so there is no body-based search either.
 *
 * SECURITY: needs no token — verified by sending no Authorization header. All
 * 113 customers' names, emails, phone numbers and dates of birth are readable
 * by anyone who knows the URL. Not fixable here; the route needs the same auth
 * middleware as its neighbours.
 */
export const GetCustomerHistoryApi = (params?: {
  page?: number;
  limit?: number;
  cmr_phone_number?: string;
}) => {
  const queryParams = new URLSearchParams();
  if (params?.page) queryParams.append('page', params.page.toString());
  if (params?.limit) queryParams.append('limit', params.limit.toString());
  if (params?.cmr_phone_number?.trim()) {
    queryParams.append('cmr_phone_number', params.cmr_phone_number.trim());
  }
  const queryString = queryParams.toString();
  return api.get(`/auth/customer/list${queryString ? `?${queryString}` : ''}`);
};

/**
 * A customer's food-log photos, for the Food Images tab on their profile.
 *
 *   POST /food-log/admin/list-admin   { search_string, cmr_id, page, limit }
 *
 * Probed live 10 Sep 2026. Each row is one photo the customer logged from the
 * mobile app: image_url, title, description, meal_type, food_type, logged_at,
 * plus a nested `customer` summary. Requires an admin token — unlike the two
 * /auth/*\/list routes, this one does refuse an anonymous request.
 *
 * PARAMETERS, each checked against a negative control:
 *
 *   cmr_id         works — scopes to one customer (a customer with no logs and
 *                  an all-zeros id both return 0, while a real one returns 3)
 *   search_string  works — matches the title ("poori" -> 1, nonsense -> 0)
 *   page / limit   work
 *   meal_type      IGNORED. Passing "breakfast" and passing "zzz" both return
 *                  the unfiltered set, so the screen filters meal type itself
 *                  rather than sending a parameter that does nothing.
 *
 * Note the pagination envelope differs from the customer list's: it is
 * { total, limit, offset, has_more, current_page, total_pages } — snake_case,
 * with `total_pages` rather than `totalPages`.
 *
 * Read-only here, matching the Documents tab next to it. The sibling write
 * routes (/food-log/create and /food-log/update) take `cmr_id` from the caller's
 * token, so an admin using them would file the photo against their OWN account
 * rather than the customer's — which is why they are not wired up.
 * /food-log/delete exists and works (it validates food_log_id and reports a
 * missing row as HTTP 200 with status:false, code:404) if a delete is ever
 * wanted; GET /food-log/:id is the odd one out and returns a real HTTP 404.
 */
export const GetCustomerFoodLogsApi = (params: {
  cmr_id: string;
  search_string?: string;
  page?: number;
  limit?: number;
}) => {
  return api.post(`/food-log/admin/list-admin`, {
    cmr_id: params.cmr_id,
    // Sent as "" rather than omitted: that is what the collection's own example
    // does for an unfiltered call, and it is confirmed to return everything.
    search_string: params.search_string?.trim() ?? "",
    page: params.page ?? 1,
    limit: params.limit ?? 50,
  });
};

/**
 * Options for the contact filters.
 *
 * Cluster, practitioner and clinic come from the Team Management list routes.
 * The `/general/getClusters`, `/general/getPractitioners` and
 * `/general/getClinics` these used to call do not exist — re-probed 26 Aug 2026,
 * all three answer the API's "Page not found" catch-all — while
 * POST /cluster/getAll, POST /clinic/getAll and POST /practitioner/getAll return
 * real records ({ id, cluster_name } / { id, clinic_name } / { id, name }),
 * which is what the Team Management screens already read.
 */
export const GetClustersApi = () => {
  return api.post(`/cluster/getAll`);
};

export const GetPractitionersApi = () => {
  return api.post(`/practitioner/getAll`);
};

export const GetClinicsApi = () => {
  return api.post(`/clinic/getAll`);
};

/**
 * Still missing, and nothing calls them any more.
 *
 * Re-swept 27 Aug 2026 — /general/getTags, /general/getCountries,
 * /general/getStates and /general/getCities all answer "Page not found", and so
 * does every variant across /general, /setup, /admin, /customer, /cust, /master,
 * /location, /geo, /common and /lookup, under GET, POST, PUT and PATCH, singular
 * and plural. 968 combinations, no hits. The neighbouring /setup/cuisines and
 * /setup/diet do answer, so the prefix is right and these four are simply not
 * deployed.
 *
 * The Contacts filters that used to depend on them now derive their options from
 * the contact rows already on screen (see distinctOptions in pages/AllContacts),
 * which is both a working filter today and a better shape than a lookup list:
 * every option offered matches a real row, so the filter cannot select its way
 * to an empty table.
 *
 * Kept declared, with their actions and sagas, in case a canonical list is ever
 * wanted for a form that has to offer values no contact holds yet — a new
 * customer in a country no one is in. Nothing needs them for filtering.
 */
export const GetTagsApi = () => {
  return api.get(`/general/getTags`);
};

export const GetCountriesApi = () => {
  return api.get(`/general/getCountries`);
};

export const GetStatesApi = (countryId?: string) => {
  return api.get(`/general/getStates${countryId ? `?countryId=${countryId}` : ''}`);
};

export const GetCitiesApi = (stateId?: string) => {
  return api.get(`/general/getCities${stateId ? `?stateId=${stateId}` : ''}`);
};

export const GetCuisinesApi = () => {
  return api.get(`/setup/cuisines`);
};

export const GetDietApi = () => {
  return api.get(`/setup/diet`);
};
/**
 * The `/progress/*` family. None of it is deployed.
 *
 * These were written against an imagined contract ("temporary mock endpoints",
 * as the original comment put it) and never replaced. Re-probed 27 Aug 2026:
 * /progress/summary, /progress/history and /progress/entry/:id all answer the
 * API's "Page not found" catch-all under GET, POST, PUT, PATCH and DELETE
 * alike — 25 method/path combinations, no hits.
 *
 * Nothing dispatches the summary, history or delete actions. UpdateProgressEntry
 * *was* dispatched, by the edit control on the Progress and Measurements tabs,
 * which is why those tabs offered a pencil icon that could only ever fail. That
 * control is gone; see the note in UserProgress.tsx.
 *
 * There is also no update route anywhere else to point them at. The measurement
 * families that do exist were swept on the same date:
 *
 *     POST /cust/measure/deleteById                  -> "Token is required"
 *     POST /cust/measure/add                         -> "Token is required"
 *     POST /cust/measure/{updateById,update,edit,…}  -> "Page not found"  (all)
 *     POST /cust/body-measurement/deleteById         -> "Token is required"
 *     POST /cust/body-measurement/{update…,edit…}    -> "Page not found"  (all)
 *
 * So read and delete exist for measurements; update does not, in any shape.
 * Kept here — rather than deleted — because the sagas, actions and reducers are
 * all still wired to them, so landing the backend route is a one-line change to
 * whichever of these four gets a real path.
 */
export const GetProgressSummaryApi = (params: any) => {
  return api.get(`/progress/summary`, { params });
};

export const GetProgressHistoryApi = (params: any) => {
  return api.get(`/progress/history`, { params });
};

export const UpdateProgressEntryApi = (params: any) => {
  return api.put(`/progress/entry/${params.measurement_id}`, params);
};

export const DeleteProgressEntryApi = (params: any) => {
  return api.delete(`/progress/entry/${params.measurement_id}`);
};

// Program related APIs
export const UploadProgramImageApi = (file: File) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post(`/upload/programs`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
};

/**
 * A program's recorded videos — many per program, each with its own date.
 *
 * Round-tripped live 10 Sep 2026 (create, read, update, delete, then cleaned up):
 *
 *   GET  /programs/getRecordedVideos/:prog_id     admin or customer
 *   POST /programs/addRecordedVideo               admin — upsert
 *   GET  /programs/deleteRecordedVideo/:recorded_id  admin
 *
 * addRecordedVideo is ONE endpoint for both add and edit, and takes either a
 * single object or an array:
 *
 *   - no `recorded_id`  -> insert. `prog_id` + `video_link` required,
 *                          `recorded_date` optional (YYYY-MM-DD).
 *   - with `recorded_id` -> update that row's link and date.
 *
 * The array runs in one transaction, so a bad `recorded_id` rolls the whole
 * batch back with "Recorded video <id> not found" — confirmed. That is why this
 * screen sends every row in a single call rather than one request per row: a
 * partial save would leave the list half-updated with no way to tell which half.
 *
 * Two quirks worth knowing. The success message is always "Recorded video added
 * successfully", even for an update and even for an empty array, so it says
 * nothing about what happened — the response `data` is the thing to read. And
 * `video_link`/`link`/`url` and `recorded_date`/`date` are accepted as aliases;
 * the canonical names are used here.
 */
export const GetProgramRecordedVideosApi = (progId: string) => {
  return api.get(`/programs/getRecordedVideos/${progId}`);
};

export const SaveProgramRecordedVideosApi = (
  params: Array<{
    recorded_id?: string;
    prog_id?: string;
    video_link: string;
    recorded_date?: string | null;
  }>
) => {
  return api.post(`/programs/addRecordedVideo`, params);
};

/**
 * Deletes one recorded video.
 *
 * A GET, which is wrong for a destructive action — the same pattern as
 * DeleteCarouselImageApi. It is the only method routed: POST and DELETE on this
 * path both answer "Page not found" (checked 10 Sep 2026). Issued by axios as an
 * XHR rather than navigated to, so nothing prefetches it, but the exposure is a
 * URL that deletes a record when opened. Backend fix: expose DELETE, then change
 * the one line here.
 */
export const DeleteProgramRecordedVideoApi = (recordedId: string) => {
  return api.get(`/programs/deleteRecordedVideo/${recordedId}`);
};

export const CreateProgramApi = (params: any) => {
  return api.post(`/programs/createProgram`, params);
};

export const GetClinicsForProgramApi = () => {
  return api.post(`/clinic/getAll`);
};

// Session related APIs
export const UploadSessionImageApi = (file: File) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post(`/upload/sessions`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

export const CreateSessionApi = (params: any) => {
  return api.post(`/session/setSession`, params);
};

export const GetAllSessionsApi = (type: string) => {
  return api.get(`/session/getAll/${type}`);
};

export const GetSessionByIdApi = (sessionId: string) => {
  return api.get(`/session/getSession/${sessionId}`);
};

export const UpdateSessionApi = (params: any) => {
  return api.post(`/session/updateSession`, params);
};

export const CreateMealsConfigurationApi = (params: any) => {
  return api.post(`/meals/createMealsConfiguration`, params);
};

export const UploadMealsImageApi = (file: File) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post(`/upload/meals`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
};

export const GetMealsUserApi = () => {
  return api.get(`/meals/getMealsUser`);
};

export const GetMealsUserByIdApi = (mealConfigId: string) => {
  return api.get(`/meals/getMealsUserById/${mealConfigId}`);
};

export const UpdateMealConfigurationByIdApi = (params: any) => {
  return api.post(`/meals/updateMealConfigurationById`, params);
};

// Menu Item related APIs
export const UploadMenuItemImageApi = (file: File) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post(`/upload/menuItems`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
};

export const CreateMenuItemApi = (params: any) => {
  return api.post(`/meals/menuItem/addMenuItem`, params);
};

export const GetMenuItemByIdApi = (params: { menu_item_id: string }) => {
  return api.post(`/meals/menuItem/getMenuItemById`, params);
};

export const UpdateMenuItemByIdApi = (params: any) => {
  return api.post(`/meals/menuItem/updateMenuItemById`, params);
};

// ABHA related APIs
export const CheckAbhaExistsApi = (params: any) => {
  return api.post(`/abha/isAbhaExists`, params);
};

export const VerifyAbhaPhoneApi = (params: any) => {
  return api.post(`/abha/verify-phone`, params);
};

export const VerifyAbhaOtpApi = (params: any) => {
  return api.post(`/abha/verify-otp`, params);
};

// Carousel related APIs
// Third site carrying the "coursel" misspelling. Left as a literal because the
// server capitalises it mid-word here ("getCourselUrls"), so the shared CAROUSEL
// constant does not drop in cleanly — but it renames at the same time as the
// other two.
export const GetCarouselUrlsApi = () => {
  return api.get(`/getCourselUrls`);
};

export const UploadCarouselImageApi = (file: File) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post(`/upload/${CAROUSEL}`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
};

/**
 * Deletes a carousel image.
 *
 * This is a GET, which is wrong for a destructive action — a GET is meant to be
 * safe to repeat, prefetch and cache. It is the only method the server exposes:
 * verified on 2026-08-13 that `DELETE /delete/files/coursel/:file`,
 * `DELETE /delete/files/carousel/:file` and `POST /delete/files/coursel/:file`
 * all return "Page not found". Switching the method here without the backend
 * would silently stop deletions working (the API answers 404 with HTTP 200).
 *
 * Practical risk today is limited because this is issued by axios as an XHR, not
 * navigated to as a link, so browsers and crawlers will not prefetch it. The
 * exposure is a URL that deletes a file when opened.
 *
 * Backend fix: expose DELETE (or POST) on the same handler, then change the one
 * line below and retire the GET.
 */
export const DeleteCarouselImageApi = (filename: string) => {
  return api.get(`/delete/files/${CAROUSEL}/${encodeURIComponent(filename)}`);
};

/**
 * About Us — the page the mobile app shows.
 *
 * Returns the single record as an object, or null:
 *
 *     { id, content, image_url, created_at, updated_at }
 *
 * It answered an ARRAY of one until 4 Sep 2026 and was redeployed to the object
 * form the same day, so readAboutUsRecord in pages/Settings accepts both. That
 * tolerance is worth keeping: it costs three lines and survives the next change.
 *
 * Note the path is snake_case while every neighbouring route is camelCase
 * (getSocialLinks, getCourselUrls) — which is why an earlier sweep of this API
 * missed it entirely and wrongly concluded there was no About Us endpoint.
 *
 * `content` is an HTML fragment. `image_url` on the pre-existing row is an
 * absolute URL on a raw origin IP that does not resolve; redux/config re-points
 * it at the configured asset base, and new saves store a relative path.
 */
export const GetAboutUsApi = () => {
  return api.get(`/about_us`);
};

/**
 * About Us write. One upsert, no id.
 *
 * Contract as revised by the backend on 4 Sep 2026:
 *
 *     GET  /about_us          -> the single object, or null      customer
 *     POST /about_us/set      { content, image_url }             admin
 *     POST /about_us/delete   { id }                             admin
 *
 * `set` updates the existing row or creates it if there is none, and never adds
 * a second — so the console does not need to know whether a record exists, and
 * there is no id to pass. This replaces the earlier create/update pair.
 *
 * Delete is deliberately not wired. The console manages the single record the
 * mobile app reads; nothing here should be able to remove that page.
 *
 * NOT YET DEPLOYED on remnutri.nextechltd.in — re-probed 4 Sep 2026, /about_us/set
 * still answers the "Page not found" catch-all while GET /about_us returns the
 * record. Written against the contract above and unverified end to end; the saga
 * gate turns "not deployed yet" into a visible error rather than a false success.
 *
 * `image_url` is saved as the relative path the upload returns
 * ("/files/about_us/….png"). The row currently holds an absolute URL on a raw
 * origin IP that does not resolve — see the note in redux/config.
 */
export const SetAboutUsApi = (params: { content: string; image_url: string }) => {
  return api.post(`/about_us/set`, params);
};

export const UploadAboutUsImageApi = (file: File) => {
  const formData = new FormData();
  formData.append("file", file);
  return api.post(`/upload/about_us`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};

// Social Links APIs
export const GetSocialLinksApi = () => {
  return api.get(`/customer/getSocialLinks`);
};

export const SetSocialLinksApi = (params: any) => {
  return api.post(`/customer/setSocialLinks`, params);
};

// Diet Compliance APIs
export const GetDietComplianceApi = (params: { starts_from: string }) => {
  return api.post(`/admin/getDietCompliance`, params);
};

/**
 * The 4-week repeating diet plan.
 *
 * The client defines a 28-day cycle once (Week 1 = cycle days 1-7, Week 2 =
 * 8-14, Week 3 = 15-21, Week 4 = 22-28). The backend materialises it into the
 * weekly admin_diet_compliance tables, so the planner still READS through
 * getDietCompliance below — one call per week, keyed on that week's Monday.
 *
 * State of these routes, probed live 8 Sep 2026:
 *
 *   GET  /admin/dietPlan/getConfig      LIVE   -> data: null (no plan set yet)
 *   POST /admin/dietPlan/setStartDate   LIVE   -> "start_date (YYYY-MM-DD) is required"
 *   POST /admin/dietPlan/runAutoRoll    LIVE   -> "auto-roll completed"
 *   POST /admin/dietPlan/generateNext   LIVE   -> "not configured. Set a start date"
 *   GET  /admin/dietPlan/getTemplate    BROKEN -> 500 "column ft.name does not exist"
 *   POST /admin/dietPlan/setTemplate    BROKEN -> 500 "column ft.name does not exist"
 *
 * The two template routes fail inside the server's own SQL, on every body and
 * with no body at all, so no template can be read or created. `ft` is almost
 * certainly the food-types table, which exposes `food_type` (see the
 * `food_type` field on a compliance item and `food_type_id` on a menu item) —
 * not `name`. That is a one-column fix on the backend.
 *
 * Consequence for this screen: the cycle DEFINITION cannot be read, so the
 * planner anchors the four weeks on getConfig's start_date and reads each
 * week's meals from getDietCompliance, which is live and returns real data.
 * getTemplate is deliberately not wired — wiring a call that only ever 500s
 * would put an error on screen that no user can act on.
 */
/**
 * Sets the cycle's start date and materialises the first 4-week block.
 *
 *     POST /admin/dietPlan/setStartDate  { start_date: "YYYY-MM-DD" }
 *
 * Verified live 8 Sep 2026. Three things worth knowing before calling it:
 *
 *  - It SNAPS to that week's Monday. The response reports both, as
 *    `start_date` and `snapped_from`, so the UI can show what was actually set
 *    rather than what was typed.
 *  - It GENERATES four weeks into admin_diet_compliance and returns them in
 *    `weeks[]`. Each entry is either `{starts_from, ends_on, skipped: true}`
 *    for a week that already existed, or `{starts_from, ends_on,
 *    admin_compliance_id, items}` for one it created.
 *  - It is IDEMPOTENT. Calling it twice with the same date reports every week
 *    as `skipped: true` — existing weeks are never overwritten, so re-anchoring
 *    cannot destroy a plan someone has already filled in.
 *
 * Only the date format is validated: anything that is not YYYY-MM-DD comes back
 * as "start_date (YYYY-MM-DD) is required", including "07-09-2026".
 *
 * The collection notes this requires an active template. One exists
 * (template_id 2a8c0a51-…) even though GET /admin/dietPlan/getTemplate fails on
 * its own SQL, so this call works while the template cannot be read or edited.
 */
export const SetDietPlanStartDateApi = (params: { start_date: string }) => {
  return api.post(`/admin/dietPlan/setStartDate`, params);
};

export const GetDietPlanConfigApi = () => {
  return api.get(`/admin/dietPlan/getConfig`);
};

export const SetDietComplianceApi = (params: any) => {
  return api.post(`/admin/setDietCompliance`, params);
};

export const GetBookedSessionsByDateApi = (params: { date: string }) => {
  return api.post(`/session/bookedSessionsByDate`, params);
};

export const ChangeSessionStatusApi = (params: { session_id: string; status: string }) => {
  return api.post(`/session/changeStatusOfSession`, params);
};

export const AddAbhaApi = (params: any) => {
  return api.post(`/abha/add-abha`, params);
};

export const GetAllAbhaCustomersApi = () => {
  return api.get(`/abha/getAllCustomers`);
};

/**
 * Documents.
 *
 * The READ is the admin route, which is the only one that can see another
 * customer's files:
 *
 *     GET /admin/document/get?page=&limit=&search=
 *
 * It answers `data.customers[]` — one entry per customer with their profile
 * fields, a `document_count` and a nested `documents[]` — plus
 * `data.pagination { total, page, limit, totalPages }`. Verified live
 * 26 Aug 2026 (auth-gated).
 *
 * The WRITES are the customer routes from the Documents API collection, and they
 * are scoped to the token: "all routes use the `customer` middleware, so
 * `cmr_id` is taken from the access token, NOT the request body". An admin
 * calling /document/set would therefore attach the file to their own record, so
 * this console does not offer create/edit/delete — /admin/document/set, /create,
 * /add, /update/:id and /delete/:id were probed on the same date and none exist.
 * They are kept here for the day an admin-scoped write lands.
 */
export const GetDocumentsApi = (params?: { page?: number; limit?: number; search?: string }) => {
  const query = new URLSearchParams();
  if (params?.page) query.append("page", String(params.page));
  if (params?.limit) query.append("limit", String(params.limit));
  if (params?.search) query.append("search", params.search);
  const queryString = query.toString();
  return api.get(`/admin/document/get${queryString ? `?${queryString}` : ""}`);
};

export const SetDocumentApi = (params: { document_type: string; document_link: string }) => {
  return api.post(`/document/set`, params);
};

export const UpdateDocumentApi = (params: { document_id: string; document_type: string; document_link: string }) => {
  const { document_id, ...body } = params;
  return api.post(`/document/update/${document_id}`, body);
};

export const DeleteDocumentApi = (params: { document_id: string }) => {
  return api.post(`/document/delete/${params.document_id}`);
};

export const LinkAbhaDocumentApi = (params: any) => {
  return api.post(`/abha/link_document`, params);
};

export const GetItemCategoriesApi = (params: any = {}) => {
  return api.post(`/item/categories`, params);
};

export const EditCategoryApi = (params: any) => {
  return api.post(`/edit/category`, params);
};

export const CreateCategoryApi = (params: any) => {
  return api.post(`/add/category`, params);
};

export const GetItemProductsApi = () => {
  return api.get(`/item/products`);
};

export const GetProductByIdApi = (params: any) => {
  return api.post(`/item/editProduct`, params);
};

export const CreateCustomerApi = (params: any) => {
  return api.post(`/admin/cust/createCust`, params);
};

/**
 * Account deletion. Both are POST — not DELETE — and neither lives at the path
 * the create/signup handlers use.
 *
 * Verified against the live API on 6 Sep 2026:
 *
 *     POST /auth/customer/delete  { cmr_id }   -> deletes a customer
 *     POST /auth/user/delete      { user_id }  -> deletes an admin/staff user
 *
 * The two are not interchangeable: each rejects the other's key outright
 * ("cmr_id is required" / "user_id is required"), which is what pins customer to
 * /auth/customer and admin to /auth/user. DELETE on /auth/user/create and
 * /auth/customer/signup answers "Page not found" — those paths route POST only,
 * and POST creates rather than deletes.
 *
 * There is no soft delete. Bookings, payments and measurements all reference
 * cmr_id, so what happens to a deleted customer's history is a backend question
 * this console cannot see the answer to.
 */
export const DeleteCustomerAccountApi = (params: { cmr_id: string }) => {
  return api.post(`/auth/customer/delete`, params);
};

/**
 * Deletes an admin/staff login. Declared, deliberately not wired to any UI.
 *
 * Nothing in this console can produce a `user_id`. Checked 6 Sep 2026: no
 * endpoint lists admin users (10 candidates swept, all "Page not found"), and
 * practitioner records carry no user_id — passing a real practitioner id here
 * answers "User does not exist with id …", so the two id spaces are separate.
 *
 * Wiring a delete button to a practitioner id would therefore either fail or,
 * worse, match some unrelated account. It needs a user-list endpoint first.
 */
export const DeleteUserAccountApi = (params: { user_id: string }) => {
  return api.post(`/auth/user/delete`, params);
};

export const ConvertToLeadApi = (params: any) => {
  return api.post(`/cust/convertToLead`, params);
};

// Meal Management APIs
export const GetMealOptionsApi = () => {
  return api.get(`/admin/getMealOptions`);
};

export const GetMealManagementOrdersApi = (params: { meal_type: string; meal: string }) => {
  return api.post(DELIVERY_MEALS, params);
};

// Guidelines APIs
export const GetGuidelinesApi = (params: { cmr_id: string }) => {
  return api.post(`/admin/getGuidelines`, params);
};

export const SetOrUpdateGuidelinesApi = (params: { cmr_id: string; guidelines: Array<{ title: string; subtitle: string }> }) => {
  // Send body with cmr_id and guideline array (note: key is "guideline" not "guidelines")
  return api.post(`/admin/setOrUpdateGuidelines`, {
    cmr_id: params.cmr_id,
    guideline: params.guidelines
  });
};

// Booked Sessions by Customer API
export const GetBookedSessionsByCustomerApi = (params: { cmr_id: string }) => {
  return api.post(`/session/bookedSessionsByCustomer`, params);
};

// Health Feed APIs
/**
 * Shared image upload — health feed banners and video thumbnails.
 *
 *     POST /upload/transformation   multipart/form-data, field `file`
 *     -> { data: { url, filename, uploadedOn, fileType } }
 *
 * `url` comes back root-relative ("/files/transformation/1787828384010.png"),
 * so pass it through assetSrc() before putting it in an <img>.
 *
 * The "transformation" bucket is the one provisioned for this — neither health
 * feeds nor videos have a bucket of their own. Note that `/upload/:type` is a wildcard that
 * authenticates before validating the type, so an unprovisioned name would
 * answer identically to a real one right up until the file went nowhere; this
 * name is confirmed working.
 */
export const UploadImageApi = (file: File) => {
  const formData = new FormData();
  formData.append("file", file);
  return api.post(`/upload/transformation`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};

export const CreateHealthFeedApi = (params: any) => {
  return api.post(`/healthFeed/createHealthFeed`, params);
};

export const GetAllHealthFeedApi = () => {
  return api.get(`/healthFeed/getAllHealthFeed`);
};

export const DeleteHealthFeedApi = (id: string) => {
  return api.get(`/healthFeed/removeHealthFeed/${id}`);
};

/**
 * `getHealthFeed` (no "ById") is the customer-facing list route: it ignores a
 * trailing id and answered `{ data: [] }` here, so Edit opened with an empty
 * body. The mobile app's blog detail screen uses this route; it returns
 * `data: [record]`.
 */
export const GetHealthFeedByIdApi = (id: string) => {
  return api.get(`/healthFeed/getHealthFeedById/${id}`);
};

/**
 * PATCH, not POST — this is the only method the route answers.
 *
 * Every other health-feed route is a POST or a GET, so this one was written as a
 * POST to match its siblings and blog editing has never worked: the call landed
 * on the API's catch-all and the screen showed "Failed to update health feed"
 * every time.
 *
 * Verified 27 Aug 2026. GET, POST, PUT and DELETE on this path all answer
 * "Page not found"; only PATCH reaches the auth middleware:
 *
 *     PATCH /healthFeed/updateHealthFeed/:id     -> "Token is required"
 *     PATCH /healthFeed/claude-probe-nope/:id    -> "Page not found"
 *
 * The control on the second line matters — this backend answers 404 with HTTP
 * 200, so "a response came back" proves nothing on its own. PATCH with a stale
 * token returns "Invalid or Expried Token", which is the auth middleware, so the
 * route is really there.
 */
export const UpdateHealthFeedApi = (id: string, params: any) => {
  return api.patch(`/healthFeed/updateHealthFeed/${id}`, params);
};

/**
 * Program purchases — what the Payments tab reads.
 *
 *     POST /programs/buyProgramListAdmin   { cmr_id?, page?, limit?, offset? }
 *
 * The admin view of the purchase ledger: `data.items[]`, each row a transaction
 * with its `program` and `customer` nested, plus
 * `pagination { total, limit, offset, has_more, current_page, total_pages }`.
 *
 * POST only — verified 26 Aug 2026: GET on this path answers the API's "Page not
 * found" catch-all, POST reaches the auth middleware. It replaces
 * /programs/buyProgramList, the mobile client's own call, which was scoped to
 * the token holder and so could never show an admin another customer's
 * purchases.
 *
 * cmr_id is sent in case the endpoint filters server-side; the tab also filters
 * the rows it gets back, so either behaviour is fine.
 */
export const GetCustomerPaymentsApi = (params: {
  cmr_id?: string;
  page?: number;
  limit?: number;
  offset?: number;
}) => {
  return api.post(`/programs/buyProgramListAdmin`, params);
};

/**
 * Meal Management's customer list.
 *
 * Verified live 19 Aug 2026: POST responds auth-gated; GET returns
 * "Page not found". Not present in the local backend checkout, so this endpoint
 * is newer than that source and its request/response shape has not been read —
 * `params` is passed straight through until the contract is confirmed.
 *
 * Replaces getDeilveryMeals for this screen, whose delivery rows are never
 * generated (its guard tests exists.rowCount, which is always 1).
 */
export const GetAllDietComplianceApi = (params: any = {}) => {
  return api.post(`/admin/getAllDietCompliance`, params);
};

/**
 * Promo codes (the Offers & Discounts list).
 *
 * Found via the RemNutri mobile client, which already consumes this endpoint for
 * its cart promo-code sheet (lib/dao/shop_dao.dart). Verified live 18 Aug 2026:
 * responds auth-gated. Returns promo_id, code, description, type,
 * min_order_value, discount_type, discount_value.
 *
 * Read only — there is no create, update or delete counterpart. Probed as both
 * GET and POST on addPromo, createPromo, setPromo, updatePromo and deletePromo;
 * all return "Page not found".
 */
export const GetPromosApi = () => {
  return api.get(`/promo/getPromos`);
};

export const GetListOfEnrollmentsApi = (params: any) => {
  return api.post(`/question/listOfEnrollments`, params);
};

export const GetEnrollmentResponseApi = (params: any) => {
  return api.post(`/question/getResponse`, params);
};